import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { ArrowRight, PenLine } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/posts`)
      .then((res) => setPosts(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="blog-page">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <p className="font-mono text-xs sm:text-sm font-medium uppercase tracking-[0.2em] text-amber-700">Blog</p>
          <h1 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900" data-testid="blog-heading">
            Notes on AI, growth, and adtech
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-stone-600 leading-relaxed">
            What I'm learning from building tools and leading growth across India &amp; SEA.
          </p>
        </div>
        {user && (
          <Link
            to="/admin/posts/new"
            data-testid="blog-new-post-button"
            className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-sm font-semibold text-stone-700 transition-all hover:border-amber-500 hover:text-amber-700"
          >
            <PenLine className="h-4 w-4" />
            Write a post
          </Link>
        )}
      </div>

      <div className="mt-12 space-y-6" data-testid="blog-post-list">
        {loading && <p className="text-stone-500" data-testid="blog-loading">Loading posts...</p>}
        {error && <p className="text-red-600" data-testid="blog-error">Couldn't load posts. Please try again later.</p>}
        {!loading && !error && posts.length === 0 && (
          <p className="text-stone-500" data-testid="blog-empty">No posts yet — the first one is on its way.</p>
        )}
        {posts.map((post) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            data-testid={`blog-post-card-${post.slug}`}
            className="group flex flex-col sm:flex-row gap-6 rounded-3xl border border-stone-200 bg-white p-8 sm:p-10 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md hover:border-amber-300"
          >
            {post.cover_image && (
              <img
                src={post.cover_image}
                alt={post.title}
                data-testid={`blog-post-cover-${post.slug}`}
                className="w-full sm:w-48 h-40 sm:h-32 rounded-2xl object-cover border border-stone-100 shrink-0"
              />
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="rounded-full bg-indigo-50 px-3 py-1 font-semibold text-indigo-800" data-testid={`blog-post-category-${post.slug}`}>
                  {post.category}
                </span>
                <span className="font-mono text-stone-400">
                  {format(new Date(post.created_at), "MMMM d, yyyy")} · {post.read_time}
                </span>
              </div>
              <h2 className="mt-4 font-heading text-xl sm:text-2xl font-semibold text-stone-900 transition-colors group-hover:text-amber-700">
                {post.title}
              </h2>
              <p className="mt-3 text-base text-stone-600 leading-relaxed">{post.excerpt}</p>
              <p className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-amber-700">
                Read the post
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
